import type { AccruedInterests } from '@fortem-monorepo/web-sdk'

type Draft = {
  revision: string
  override: AccruedInterests.BalanceOverride
}
export type ScopedBalanceDrafts = Record<string, Draft>
export type BalanceDrafts = Record<string, ScopedBalanceDrafts>
export type BalanceDisplayRow = AccruedInterests.BalanceRow & {
  override?: AccruedInterests.BalanceOverride
  stale?: boolean
}

const scopeKey = ({ fund_name, value_date }: AccruedInterests.Params) => JSON.stringify([fund_name, value_date])

/** Keep pending edits isolated by exact fund/date scope. */
export const getBalanceDrafts = (drafts: BalanceDrafts, params: AccruedInterests.Params): ScopedBalanceDrafts =>
  drafts[scopeKey(params)] ?? {}

/** Retain the source revision used for review, rather than silently rebasing a correction. */
export const stageBalanceDraft = (
  drafts: BalanceDrafts,
  params: AccruedInterests.Params,
  row: AccruedInterests.BalanceRow,
  override: AccruedInterests.BalanceOverride,
): BalanceDrafts => ({
  ...drafts,
  [scopeKey(params)]: {
    ...getBalanceDrafts(drafts, params),
    [row.trade_id]: { revision: row.revision, override: { ...override } },
  },
})

/** Clear a scope or only the trade IDs whose batch was confirmed successfully. */
export const clearBalanceDrafts = (
  drafts: BalanceDrafts,
  params: AccruedInterests.Params,
  tradeIds?: string[],
): BalanceDrafts => {
  const key = scopeKey(params)
  const remaining = { ...drafts[key] }
  if (tradeIds) {
    tradeIds.forEach((id) => delete remaining[id])
  }
  const next = { ...drafts }
  if (tradeIds && Object.keys(remaining).length) next[key] = remaining
  else delete next[key]
  return next
}

/** Overlay local values while marking corrections whose server inputs have changed. */
export const withBalanceDrafts = (
  rows: AccruedInterests.BalanceRow[],
  drafts: ScopedBalanceDrafts,
): BalanceDisplayRow[] =>
  rows.map((row) => {
    const draft = Object.hasOwn(drafts, row.trade_id) ? drafts[row.trade_id] : undefined
    return draft
      ? {
          ...row,
          ...draft.override,
          revision: draft.revision,
          override: { ...draft.override },
          stale: draft.revision !== row.revision,
        }
      : row
  })

type DisplayNode = { group?: boolean; data?: BalanceDisplayRow }

/** Freeze all filtered leaf rows, including children inside collapsed groups. */
export const freezeDisplayedBalances = (grid: {
  forEachNodeAfterFilterAndSort: (visit: (node: DisplayNode) => void) => void
}): BalanceDisplayRow[] => {
  const rows: BalanceDisplayRow[] = []
  grid.forEachNodeAfterFilterAndSort((node) => {
    if (node.group || !node.data) return
    rows.push({
      ...node.data,
      coupon_dates: [...node.data.coupon_dates],
      ...(node.data.override ? { override: { ...node.data.override } } : {}),
    })
  })
  return rows
}

/** Submit reviewed identities/revisions and explicit overrides, never display-only metadata. */
export const toConfirmationRows = (rows: BalanceDisplayRow[]): AccruedInterests.ConfirmationRow[] =>
  rows.map(({ trade_id, revision, override }) => ({
    trade_id,
    revision,
    ...(override ? { override: { ...override } } : {}),
  }))
